import {
  useApp,
  useCart,
  useDealer,
  useIsAdminMode,
  useSettings,
} from "@/contexts";
import { CartHelper } from "@/helpers";
import { CartProduct, Product } from "@/interfaces";
import { colors } from "@/theme";
import SearchIcon from "@mui/icons-material/Search";
import { Box, Button, TextField, useMediaQuery, useTheme } from "@mui/material";
import InputAdornment from "@mui/material/InputAdornment";
import { FormEvent, useEffect, useState } from "react";
// import * as S from "./styles";
import ProductTable from "@/components/organisms/ProductTable";
import { useDataApi } from "@/hooks";
import { formatCurrency } from "@/utils";
import AddIcon from "@mui/icons-material/Add";
import BorderColorOutlinedIcon from "@mui/icons-material/BorderColorOutlined";
import CancelIcon from "@mui/icons-material/Cancel";
import CloseIcon from "@mui/icons-material/Close";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import RemoveIcon from "@mui/icons-material/Remove";
import TuneIcon from "@mui/icons-material/Tune";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import SavedOrderDialog from "../../molecules/SavedOrderDialog";

const baseUrl = `${process.env.REACT_APP_REST_BASE_URL}/products`;


const OffSiteProducts: React.FC = () => {
  const {
    cart,
    setDiscount,
    setCart,
    add,
    setDiscountTypeWithCartType,
    setQuantity: setProductItemQuantity,
    remove,
    setProductPrice,
    setDiscountComment
  } = useCart();
  const { settings } = useSettings();
  const { dealer, coopInfo } = useDealer();
  const [isOrderAdjustmentOpen, setIsOrderAdjustmentOpen] = useState(false);
  const [discount, setDiscountWithType] = useState({
    type: dealer?.percentageOff ? 'percentage' : 'amount' as 'percentage' | 'amount',
    value: dealer?.percentageOff ? dealer.percentageOff * 100 : 0
  });
  const [discountNote, setDiscountNote] = useState("");


  const [searchValue, setSearchValueLocal] = useState<string>("");
  const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);
  const [isSavedOrderDialogOpen, setIsSavedOrderDialogOpen] = useState(false);
  const [savedOrder, setSavedOrder] = useState<any>(null);
  const {
    data: productsData,
    isLoading: isProductsLoading,
    error: productsError,
    setUrl: setProductsUrl,
  } = useDataApi();
  const navigate = useNavigate();
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [editPrice, setEditPrice] = useState<string>("");

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [productToDelete, setProductToDelete] = useState<CartProduct | null>(null);

  // Filter for offsite products only
  const offsiteProducts =
    cart?.products?.filter((p) => p.offsite) || [];
  const offsiteCart = cart ? { ...cart, products: offsiteProducts } : undefined;
  const orderOffSite = (offsiteCart as any)?.offsite
  console.log({ offsiteCart, orderOffSite })

  const [discountValue, setDiscountValue] = useState(formatCurrency(offsiteCart?.offSiteDiscount ?? 0));
  const [discountAmount, setDiscountAmount] = useState(formatCurrency(cart?.offSiteDiscount ?? 0));

  const handleContinueSaved = async () => {
    if (!savedOrder?.orderId) {
      console.log('No saved order ID available');
      return;
    }
    if (settings?.shippingFee == null) {
      // Wait for settings to load, or show a loading spinner
      alert("Settings not loaded yet. Please try again in a moment.");
      return;
    }
    try {
      console.log('Fetching full order details for order:', savedOrder.orderId);
      const response = await axios.get(
        `${process.env.REACT_APP_REST_BASE_URL}/Orders/getorderfull/${savedOrder.orderId}`
      );
      console.log('Full order details received:', response.data);
      setCart?.({
        ...response.data,
        shippingFee: settings.shippingFee,
        coopEligible: settings.coopEligible,
        // coopAmount: settings.couponAmount,
      });
      console.log('Cart after setting shipping fee:', settings);
      setIsSavedOrderDialogOpen(false);
    } catch (error) {
      console.error("Error loading saved order:", error);
      if (axios.isAxiosError(error)) {
        console.error("Error details:", {
          status: error.response?.status,
          data: error.response?.data,
          message: error.message,
        });
      }
    }
  };

  const getTotalAmount = () => (CartHelper.getTotalAmount({ ...offsiteCart, products: offsiteCart?.products || [], shippingFee: 0 }) + (parseFloat(discountValue) || 0))

  const handleUpdateItem = (e: FormEvent) => {
    e.preventDefault();
    if (editingProduct && !isNaN(Number(editPrice))) {
      // Update the product price in the cart
      const updatedProducts = cart?.products.map(p => {
        if (p.productId === editingProduct.productId) {
          setProductPrice(p.productId, Number(editPrice))

          return { ...p, price: Number(editPrice) }
        }

        else return p
      }
      );
      setCart?.({ ...cart, products: updatedProducts! });
    }
    setIsEditDialogOpen(false);
  }

  const handleNewOrder = async () => {
    if (!savedOrder?.orderId) {
      console.log('No saved order ID available');
      return;
    }
    setIsSavedOrderDialogOpen(false);
    setCart?.(undefined);
  };

  // Fetch products on component mount
  useEffect(() => {
    const url = `${baseUrl}?offsite=true`;
    setProductsUrl(url);
  }, []);

  // Update products when data is fetched
  useEffect(() => {
    if (productsData?.data) {
      setFilteredProducts(productsData.data);
      console.log("Fetched products:", productsData.data);

      // Auto-add product if there's only one result and it matches the search value
      if (productsData.data.length === 1 && productsData.data[0].sku === searchValue) {
        const product = productsData.data[0];
        // Only allow adding if cart is empty or product type matches cart
        const isCartEmpty = CartHelper.isEmpty(cart);
        if (
          isCartEmpty ||
          (product.offsite)
        ) {
          add?.(
            product,
            settings?.shippingFee,
            settings?.claimFee,
            // product.offsite
            //   ? coopInfo?.reimbursementRateOffSite
            //   : coopInfo?.reimbursementRateInStore,
            settings?.coopEligible,
            settings?.couponAmount
          );
          // Clear search after adding
          setSearchValueLocal("");
        }
      }
    }
  }, [productsData, searchValue, cart, add, settings, coopInfo]);

  // Handle search input changes with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      console.log("searchValue:", searchValue);
      const url = `${baseUrl}?search=${searchValue}&offsite=true`;
      setProductsUrl(url);
    }, 400); // 500ms debounce

    return () => clearTimeout(timer);
  }, [searchValue]);

  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValueLocal(e.target.value);
  };

  const handleCancelClick = () => {
    setSearchValueLocal("");
    const url = `${baseUrl}?offsite=true`;
    setProductsUrl(url);
  };

  const handleAddDiscount = () => {
    let discountAmount = 0
    const total = Number(CartHelper.getSubtotalBalance(offsiteCart) || 0);
    console.log({ total })
    if (discount.type == "percentage") {
      const discountPercentage = discount.value || 0;
      discountAmount = total * discountPercentage / 100
    }
    else {
      discountAmount = discount.value || 0;
    }
    if (discountAmount > total) {
      alert("Discount cannot be greater than the subtotal amount");
      return;
    }
    setDiscountValue(`${discountAmount}`)
    if (setDiscount) {
      setDiscountTypeWithCartType?.({ storeType: "in-store", value: discount.value ?? 0, type: discount.type })
      setDiscountComment?.(discountNote)
      setDiscount(discountAmount, true);
      if (offsiteCart) {
        offsiteCart.offSiteDiscount = discountAmount;
      }
    }
    setIsOrderAdjustmentOpen(false);
  }

  const handleProductClick = (product?: Product) => {
    // if (!product || (typeof product.inventoryCount === "number" && product.inventoryCount <= 0)) {
    //   alert("This product is out of Stock.");
    //   return;
    // }

    // this check added to find if the product is already in cart
    const cartProduct = offsiteCart?.products.find(p => p.productId == product?.productId)
    console.log("products:", product);
    console.log({ cartProduct })
    if (cartProduct) setProductItemQuantity?.(
      cartProduct.productId,
      (cartProduct.quantity || 0) + 1,
      "in-store"
    )
    else add?.(
      product,
      settings?.shippingFee,
      settings?.claimFee,
      settings?.coopEligible,
      settings?.couponAmount,
      true
    );
  };

  const handleOrderAdjustmentOpen = () => {
    setDiscountAmount(formatCurrency(cart?.offSiteDiscount ?? 0));
    setIsOrderAdjustmentOpen(true);
  };

  const handleCheckoutClick = () => {
    navigate("/checkout", { state: { mode: "offsite" } });
  };

  useEffect(() => {
    if (discount.type == "percentage") handleAddDiscount()
  }, [CartHelper.getSubtotalBalance(offsiteCart)])

  return (
    <Box
      sx={{
        width: "100%",
        height: "calc(100% - 64px)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        bgcolor: "#f0f0f0",
        position: "fixed",
        top: 64,
        left: 0,
        right: 0,
        bottom: 0,
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          width: "97%",
          height: "95%",
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          gap: 3,
          p: 2,
          borderRadius: 3,
          overflow: "auto",
          background: "transparent",
          position: "relative",
          "@media (max-width: 1068px)": {
            flexDirection: "column"
          }
        }}
      >
        {/* Left Column */}
        <Box
          sx={{
            flex: { xs: "none", md: 2 },
            display: "flex",
            flexDirection: "column",
            gap: 3,
            height: { xs: "auto", md: "100%" },
            minHeight: { xs: "40vh", md: "auto" },
            overflow: "hidden",
            "@media (max-width: 1068px)": {
              flex: "none",
              height: "60vh",
              // minHeight: ""
            }
          }}
        >
          {/* Top: Input Field */}
          <Box
            sx={{
              p: { xs: 1, md: 2 },
              background: "#f5f6fa",
              borderRadius: 2,
              mb: 0,
              minHeight: 48,
              display: "flex",
              alignItems: "center",
              boxShadow: 0,
            }}
          >
            <TextField
              fullWidth
              autoFocus
              placeholder="Search Off-Site Products"
              variant="standard"
              size="small"
              value={searchValue}
              onChange={handleSearchInputChange}
              InputProps={{
                disableUnderline: true,
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon
                      sx={{ color: colors.neutralGrey800, fontSize: 24 }}
                    />
                  </InputAdornment>
                ),
                endAdornment: searchValue ? (
                  <InputAdornment position="end">
                    <CancelIcon
                      sx={{ color: colors.primaryOzone500, cursor: "pointer" }}
                      onClick={handleCancelClick}
                      style={{ width: 24, height: 24 }}
                    />
                  </InputAdornment>
                ) : null,
                sx: {
                  border: "none",
                  background: "transparent",
                  paddingLeft: 0,
                  height: 40,
                  alignItems: "center",
                },
              }}
              sx={{
                border: "none",
                background: "transparent",
                boxShadow: "none",
                paddingLeft: 0,
                "& .MuiInputBase-input::placeholder": {
                  color: colors.neutralGrey800,
                  opacity: 1,
                  fontSize: "20px",
                  fontFamily: "PF DIN Text Pro",
                  lineHeight: "24px",
                },
                "& .MuiInputBase-input": {
                  height: "40px",
                  fontSize: "20px",
                  fontFamily: "PF DIN Text Pro",
                  display: "flex",
                  fontWeight: "400",
                  alignItems: "center",
                  padding: 0,
                },
              }}
            />
          </Box>
          {/* Bottom: Products List */}
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
              background: "#fff",
              borderRadius: 2,
              boxShadow: 2,
              p: { xs: 1, md: 2 },
              overflow: "auto",
            }}
          >
            {isProductsLoading ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100%",
                  fontSize: "24px",
                  fontWeight: 500,
                  fontFamily: "PF DIN Text Pro",
                }}
              >
                <span>Loading...</span>
              </Box>
            ) : productsError ? (
              <Box sx={{ color: "red", textAlign: "center", mt: 2 }}>
                Error loading products
              </Box>
            ) : filteredProducts.length === 0 ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100%",
                  color: colors.neutralGrey600,
                  fontSize: "20px",
                  fontFamily: "PF DIN Text Pro",
                }}
              >
                {searchValue
                  ? "No Product Found"
                  : "No Off-Site Products are currently available"}
              </Box>
            ) : (
              <ProductTable
                items={filteredProducts}
                defaultProductImageUrl={settings?.defaultProductImageUrl}
                coopEligible={coopInfo?.reimbursementRateInStore}
                onItemClick={handleProductClick}
              />
            )}
          </Box>
        </Box>
        {/* Right Column */}
        <Box
          sx={{
            flex: { xs: "none", md: 1 },
            display: "flex",
            flexDirection: "column",
            gap: 3,
            height: { xs: "auto", md: "100%" },
            minHeight: { xs: "30vh", md: "auto" },
            overflow: "hidden",
            "@media (max-width: 1068px)": {
              flex: "none",
              height: "auto",
              minHeight: "30vh"
            }
          }}
        >
          {/* Top: User Info */}
          <Box
            sx={{
              p: { xs: 1, md: 2 },
              background: "#f5f6fa",
              borderRadius: 2,
              minHeight: 48,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              boxShadow: 0,
            }}
          >
            <div style={{ fontWeight: 600 }}>
              {dealer?.firstName ?? ""} {dealer?.lastName ?? ""}-{dealer?.company ?? ""}
            </div>
            <div style={{ fontSize: 12, color: "#888" }}>
              {dealer?.channels?.at(0)?.channel} - {" "}
              {dealer?.channels?.at(0)?.masterDealerId} {" "} | {" "}
              {formatCurrency(dealer?.channels?.at(0)?.available || 0)}{" "}
              Co-Op Available
            </div>
          </Box>
          {/* Bottom: Purchase Summary */}
          <Box
            sx={{
              fontFamily: "PF DIN Text Pro",
              flex: 1,
              background: "#fff",
              borderRadius: 2,
              boxShadow: 2,
              p: { xs: 1.5, md: 3 },
              display: "flex",
              flexDirection: "column",
              height: "100%",
              overflow: "hidden",
            }}
          >
            {/* Header */}
            <Box
              sx={{
                fontWeight: 600,
                marginBottom: 2,
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                justifyContent: "space-between",
                alignItems: { xs: "flex-start", sm: "center" },
                fontSize: 16,
                borderBottom: `1px solid ${colors.neutralGrey400}`,
                paddingBottom: 2,
                gap: { xs: 1, sm: 0 },
                flexShrink: 0,
              }}
            >
              <Box
                sx={{
                  fontWeight: 500,
                  fontSize: { xs: 20, sm: 24 },
                  color: colors.neutralGrey800,
                }}
              >
                Purchase Summary
              </Box>
              <Box
                sx={{
                  fontWeight: 400,
                  color: colors.neutralGrey600,
                  fontSize: "16px",
                  fontFamily: "PF DIN Text Pro",
                }}
              >
                {`Order Number #${orderOffSite ? (cart?.orderId ?? "") : ""} `}
              </Box>
            </Box>

            {/* Product List */}
            <Box
              sx={{
                flex: 1,
                overflow: "auto",
                minHeight: 0,
                display: "flex",
                flexDirection: "column"
              }}
            >
              {offsiteProducts.map((product) => (
                <Box
                  key={product.productId}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    borderBottom: `1px solid ${colors.neutralGrey300}`,
                    py: 1.5,
                    px: 0,
                  }}
                >
                  {/* Image */}
                  <img
                    src={
                      product.productImageUrl ||
                      settings?.defaultProductImageUrl
                    }
                    alt={product.productDescription}
                    style={{
                      width: 56,
                      height: 56,
                      objectFit: "contain",
                      marginRight: 14,
                    }}
                  />
                  {/* Info */}
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box
                        sx={{
                          fontSize: 16,
                          fontWeight: 500,
                          color: colors.neutralGrey800,
                        }}
                      >
                        {product.productDescription}
                      </Box>
                      <BorderColorOutlinedIcon
                        sx={{
                          fontSize: 16,
                          color: colors.neutralGrey600,
                          cursor: "pointer",
                        }}
                        onClick={() => {
                          setEditingProduct(product);
                          setEditPrice(product.price?.toFixed(2) || "");
                          setIsEditDialogOpen(true);
                        }}
                      />
                    </Box>
                    <Box
                      sx={{
                        fontSize: 13,
                        color: colors.neutralGrey600,
                        mb: 0.5,
                      }}
                    >
                      SKU# {product.sku}
                    </Box>
                    <Box sx={{ display: "flex", gap: 2, mb: 0.5 }}>
                      <span
                        style={{ fontSize: 13, color: colors.neutralGrey800 }}
                      >
                        ${(product.price || 0).toFixed(2)} Retail
                      </span>
                      <span
                        style={{ fontSize: 13, color: colors.neutralGrey800 }}
                      >
                        {formatCurrency((product.price || 0) - ((product.price || 0) * (settings?.coopEligible || 0)))}{" "}
                        w/ Co-Op
                      </span>
                    </Box>
                    {/* Quantity Controls */}
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <IconButton
                        size="small"
                        onClick={() => {
                          if ((product?.quantity || 0) > 1) {
                            setProductItemQuantity?.(
                              product.productId,
                              (product?.quantity || 0) - 1,
                              "in-store"
                            );
                          } else {
                            setProductToDelete(product);
                            setIsDeleteDialogOpen(true);
                          }
                        }}
                        sx={{ borderRadius: "50%", p: "4px" }}
                      >
                        {(product.quantity || 0) > 1 ? (
                          <RemoveIcon fontSize="small" />
                        ) : (
                          <DeleteOutlineIcon fontSize="small" />
                        )}
                      </IconButton>
                      <span
                        style={{
                          fontSize: 16,
                          fontWeight: 500,
                          minWidth: "24px",
                          textAlign: "center",
                        }}
                      >
                        {product.quantity || 0}
                      </span>
                      <IconButton
                        size="small"
                        onClick={() =>
                          setProductItemQuantity?.(
                            product.productId,
                            (product.quantity || 0) + 1,
                            "in-store"
                          )
                        }
                        sx={{ borderRadius: "50%", p: "4px" }}
                      >
                        <AddIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                  {/* Price Block */}
                  <Box sx={{ minWidth: 110, textAlign: "right" }}>
                    <Box sx={{ fontSize: 13, color: colors.neutralGrey800 }}>
                      {formatCurrency((product?.price || 0) * (product.quantity || 1))}{" "}
                      Retail
                    </Box>
                    <Box sx={{ fontSize: 13, color: colors.neutralGrey800 }}>
                      -
                      {formatCurrency(product.coopApplied || 0)}{" "}
                      Co-Op
                    </Box>
                    <Box
                      sx={{
                        fontSize: 20,
                        fontWeight: 600,
                        color: colors.primaryOzone500,
                      }}
                    >
                      {formatCurrency(
                        ((product?.price || 0) * (product.quantity || 0)) - (product.coopApplied || 0)
                      )}
                    </Box>
                  </Box>
                </Box>
              ))}
            </Box>

            {/* Total and Buttons */}
            <Box
              sx={{
                borderTop: `1px solid ${colors.neutralGrey400}`,
                mt: 2,
                pt: 2,
                flexShrink: 0,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-end",
                  mb: 2,
                }}
              >
                <Box
                  sx={{
                    color: colors.neutralGrey800,
                    fontSize: 20,
                    fontWeight: 500,
                  }}
                >
                  Total{" "}
                  <Box component="span" sx={{ fontSize: 14, fontWeight: 400 }}>
                    ({CartHelper.getTotalQuantity(offsiteCart)} items)
                  </Box>
                </Box>
                <Box
                  sx={{
                    color: colors.primaryOzone500,
                    fontWeight: 600,
                    fontSize: 28,
                    lineHeight: 1,
                  }}
                >
                  {
                    formatCurrency(CartHelper.getTotalAmount({ ...offsiteCart, products: offsiteCart?.products || [], shippingFee: 0 }, true))
                  }

                </Box>
              </Box>
              <Button
                variant="outlined"
                fullWidth
                startIcon={<TuneIcon sx={{ height: 24, width: 24 }} />}
                sx={{
                  mb: 1.5,
                  fontWeight: 500,
                  fontSize: 18,
                  fontFamily: "PF DIN Text Pro",
                  borderRadius: 2,
                  borderColor: colors.neutralGrey400,
                  color: colors.neutralGrey800,
                  textTransform: "none",
                  py: 1.5,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
                onClick={handleOrderAdjustmentOpen}
              >
                Order Level Discount
              </Button>
              <Button
                variant="contained"
                fullWidth
                sx={{
                  background: colors.primaryOzone500,
                  color: colors.white,
                  fontWeight: 500,
                  fontSize: 18,
                  fontFamily: "PF DIN Text Pro",
                  borderRadius: 2,
                  textTransform: "none",
                  py: 1.5,
                  boxShadow: "none",
                  ":hover": { background: "#0059c1" },
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
                onClick={handleCheckoutClick}
              >
                <CreditCardIcon sx={{ mr: 1, height: 24, width: 24 }} />
                Checkout
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Order Level Adjustment Dialog */}
      <Dialog
        open={isOrderAdjustmentOpen}
        onClose={() => setIsOrderAdjustmentOpen(false)}
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: '16px',
            p: 0,
            background: '#fff',
            boxShadow: 24,
            minWidth: 400,
            width: 400,
            overflow: 'visible',
          },
        }}
      >
        <Box sx={{ p: 0, width: '100%' }}>
          {/* Title and Close */}
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            pt: 3,
            pb: 2,
          }}>
            <Typography sx={{
              fontWeight: 700,
              fontSize: 22,
              color: '#223354',
              fontFamily: 'PF DIN Text Pro',
              textAlign: 'center',
              flex: 1,
            }}>
              Order Level Discount
            </Typography>
            <IconButton
              onClick={() => setIsOrderAdjustmentOpen(false)}
              sx={{
                position: 'absolute',
                right: 24,
                top: '50%',
                transform: 'translateY(-50%)',
                background: colors.white,

              }}
            >
              <CancelIcon
                sx={{ color: colors.primaryOzone500, cursor: "pointer" }}
                style={{ width: 30, height: 30 }}
              />
            </IconButton>
          </Box>
          {/* Divider */}
          <Box sx={{ borderBottom: '1px solid #E0E0E0', mb: 0.5 }} />
          {/* Tabs */}
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mt: 1,
            mb: 3,
            position: 'relative',
          }}>
            <Box
              sx={{
                fontWeight: discount.type === 'amount' ? 700 : 500,
                fontSize: 18,
                color: discount.type === 'amount' ? '#223354' : '#A0A4AD',
                fontFamily: 'PF DIN Text Pro',
                px: 2,
                pb: 1,
                cursor: 'pointer',
                borderBottom: discount.type === 'amount' ? '3px solid #0074FF' : '3px solid transparent',
                transition: 'border-color 0.2s',
                mr: 2,
              }}
              onClick={() => setDiscountWithType(prev => ({ ...prev, type: "amount" }))}
            >
              Amount
            </Box>
            <Box
              sx={{
                fontWeight: discount.type === 'percentage' ? 700 : 500,
                fontSize: 18,
                color: discount.type === 'percentage' ? '#223354' : '#A0A4AD',
                fontFamily: 'PF DIN Text Pro',
                px: 2,
                pb: 1,
                cursor: 'pointer',
                borderBottom: discount.type === 'percentage' ? '3px solid #0074FF' : '3px solid transparent',
                transition: 'border-color 0.2s',
              }}
              onClick={() => setDiscountWithType(prev => ({ ...prev, type: 'percentage' }))}
            >
              Percentage
            </Box>
            {/* Gray divider under tabs */}
            <Box sx={{
              position: 'absolute',
              left: 0,
              right: 0,
              bottom: 0,
              height: '1px',
              bgcolor: '#E0E0E0',
              zIndex: 0,
            }} />
          </Box>
          {/* Input and Note */}
          <Box sx={{ px: 5, pb: 0, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <TextField
              fullWidth
              {...((discount.value == 0 || discount.value) ? ({ value: discount.value }) : {})}
              placeholder={'0.00'}
              onChange={(e) => {
                const value = e.target.value.replace(/[^\d.]/g, "");
                const parts = value.split('.');
                if (parts.length > 2) return;
                if (parts[1] && parts[1].length > 2) return;
                if (value.startsWith('-')) return;
                setDiscountWithType(prev => ({ ...prev, value: Number(value) }))
              }}
              InputProps={{
                startAdornment: discount.type === 'amount' ? <span style={{ marginRight: 8, color: '#B0B4BB', fontWeight: 500, fontSize: 22 }}>$</span> : undefined,
                endAdornment: discount.type === 'percentage' ? <span style={{ marginLeft: 8, color: '#B0B4BB', fontWeight: 500, fontSize: 22 }}>%</span> : undefined,
                sx: {
                  alignSelf: 'center',
                  fontSize: 22,
                  width: '50%',
                  fontWeight: 500,
                  background: '#fff',
                  fontFamily: 'PF DIN Text Pro',
                  borderRadius: '12px',
                  height: 56,
                  // pl: 1,
                  '& input': {
                    textAlign: 'center',
                    fontSize: 22,
                    fontWeight: 500,
                    color: '#223354',
                    padding: '16px 0',
                  },
                },
              }}
              sx={{
                mb: 3,
                background: '#fff',
                borderRadius: '12px',
                boxShadow: 'none',
              }}
              inputProps={{
                inputMode: 'decimal',
              }}
            />
            <TextField
              fullWidth
              multiline
              rows={3}
              value={discountNote}
              onChange={(e) => setDiscountNote(e.target.value)}
              placeholder="Enter a note"
              sx={{
                mb: 3,
                background: '#fff',
                borderRadius: '12px',
                boxShadow: 'none',
                border: '1px solid #E0E0E0',
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                  background: '#fff',
                  '& fieldset': {
                    borderColor: '#E0E0E0',
                  },
                },
                '& textarea': {
                  fontFamily: 'PF DIN Text Pro',
                  fontSize: 16,
                },
              }}
            />
          </Box>
          {/* Button */}
          <Box sx={{ px: 0, pb: 0, pt: 0 }}>
            <Button
              onClick={handleAddDiscount}
              fullWidth
              sx={{
                background: '#0074FF',
                color: '#fff',
                borderRadius: '0 0 16px 16px',
                fontWeight: 500,
                fontSize: 20,
                fontFamily: 'PF DIN Text Pro',
                py: 2,
                boxShadow: 'none',
                textTransform: 'none',
                mt: 0,
                '&:hover': { background: '#0059c1' },
              }}
            >
              Add Discount
            </Button>
          </Box>
        </Box>
      </Dialog>

      {/* Saved Order Dialog */}
      <SavedOrderDialog
        open={isSavedOrderDialogOpen}
        onClose={() => setIsSavedOrderDialogOpen(false)}
        onContinueSaved={handleContinueSaved}
        onNewOrder={handleNewOrder}
      />

      {/* Edit Item Dialog */}
      <Dialog
        open={isEditDialogOpen}
        onClose={() => setIsEditDialogOpen(false)}
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: 3,
            p: 0,
            minWidth: 350,
            fontFamily: "PF DIN Text Pro",
          },
        }}
      >
        <DialogTitle
          sx={{
            textAlign: "center",
            fontWeight: 600,
            fontSize: 18,
            pt: 3,
            pb: 1,
            px: 3,
          }}
        >
          Edit Item
          <IconButton
            aria-label="close"
            onClick={() => setIsEditDialogOpen(false)}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: colors.primaryOzone500,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <form onSubmit={handleUpdateItem}>

          {editingProduct && (
            <DialogContent sx={{ padding: 0 }}>
              <Box sx={{ p: 3 }}>
                <Box
                  sx={{
                    width: "100%",
                    height: "200px",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    mb: 3,
                    bgcolor: colors.white,
                    border: `1px solid ${colors.neutralGrey200}`,
                    borderRadius: 1,
                  }}
                >
                  <img
                    src={
                      editingProduct.productImageUrl ||
                      settings?.defaultProductImageUrl
                    }
                    alt={editingProduct.productDescription}
                    style={{
                      maxWidth: "80%",
                      maxHeight: "80%",
                      objectFit: "contain",
                    }}
                  />
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography
                    sx={{
                      fontSize: "22px",
                      fontWeight: 500,
                      color: colors.neutralGrey800,
                      mb: 1,
                      fontFamily: "PF DIN Text Pro",
                    }}
                  >
                    {editingProduct.productDescription}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "12px",
                      color: colors.neutralGrey600,
                      fontFamily: "PF DIN Text Pro",
                    }}
                  >
                    SKU# {editingProduct.sku}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    mb: 3,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "16px",
                      fontWeight: 400,
                      color: colors.neutralGrey800,
                      fontFamily: "PF DIN Text Pro",
                    }}
                  >
                    ${editingProduct.price?.toFixed(2)} Retail
                  </Typography>
                  <TextField
                    placeholder="Enter New Amount"
                    variant="outlined"
                    size="small"
                    autoFocus
                    value={editPrice}
                    onChange={e => setEditPrice(e.target.value.replace(/[^\d.]/g, ""))}
                    sx={{
                      flex: 1,
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "4px",
                        backgroundColor: colors.white,
                        "& fieldset": {
                          borderColor: colors.neutralGrey300,
                        },
                      },
                      "& input": {
                        fontFamily: "PF DIN Text Pro",
                      },
                    }}
                  />
                </Box>
              </Box>
            </DialogContent>
          )}
          <Button
            type="submit"
            variant="contained"
            fullWidth
            sx={{
              background: colors.primaryOzone500,
              color: colors.white,
              fontWeight: 500,
              fontSize: 18,
              fontFamily: "PF DIN Text Pro",
              borderRadius: "0 0 2px 2px",
              py: 1.5,
              boxShadow: "none",
              textTransform: "none",
              ":hover": { background: "#0059c1" },
            }}
          >
            Update Item
          </Button>
        </form>

      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: 3,
            p: 0,
            minWidth: 350,
            fontFamily: "PF DIN Text Pro",
          },
        }}
      >
        <DialogContent sx={{ textAlign: "center", pt: 4, pb: 2 }}>
          <Typography
            sx={{
              fontSize: 18,
              fontWeight: 500,
              mb: 2,
              fontFamily: "PF DIN Text Pro",
            }}
          >
            Are you sure you want to delete this item?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", pb: 3 }}>
          <Button
            variant="outlined"
            onClick={() => setIsDeleteDialogOpen(false)}
            sx={{
              color: colors.neutralGrey800,
              borderColor: colors.neutralGrey800,
              minWidth: 100,
              borderRadius: 1.5,
              fontWeight: 500,
              fontSize: 18,
              textTransform: "none",
              mr: 2,
              fontFamily: "PF DIN Text Pro",
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              if (productToDelete) {
                remove?.(productToDelete.productId);
              }
              setIsDeleteDialogOpen(false);
              setProductToDelete(null);
            }}
            sx={{
              minWidth: 100,
              borderRadius: 1.5,
              fontWeight: 400,
              fontSize: 18,
              textTransform: "none",
              background: "#E53912",
              color: "#fff",
              ":hover": { background: "#b71c00" },
              fontFamily: "PF DIN Text Pro",
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OffSiteProducts;
